import LabelHead from '../../../../ui/label-head';
import { useDispatch } from 'react-redux';
import { setEffectiveDate, setPolicyInformationType } from '../../../../../features/ebam/plicy-data-page/policy-information/policy-information.slice';
import PolicyCKEditorTextModal from '../../modal/policy-ck-editor-text-modal';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';
import PolicyInformationClassificationAndEligibility from './policy-information-classification-and-eligibility';
import PolicyInformationParticipation from './policy-information-participation';
import PolicyInformationUnderwritingProvisions from './policy-information-underwriting-provisions';
import { usePolicyActions } from '../../../../../hooks/actions/use-policy-actions';
import PolicyTabHeader from './policy-tab-header';
import Input from '../../../../ui/input';
import { customStyle, defaultStyle } from '../../../../../constant/custom-style';
import { useCKEditorActions } from '../../../../../hooks/actions/use-ck-editor-actionst';
import CKEditor from '../../../../../library/ckeditor/ck-editor';
import { usePolicyDataState } from '../../../../../hooks/policy/use-policy-data-state';
import { usePolicySocket } from '../../../../../hooks/socket/use-policy-socket';

export default function PolicyInformation() {

    const dispatch = useDispatch();
    const { 
        basicPlan,
        groupName,
        riders,
        PIEligibleIndividuals,
        PIClassificationAndEligibility,
        PIParticipationReq,
        PIUnderwritingProvisions,
        PISpecialUnderwritingProvisions
    } = useProposalTypeData()
    const {
        isPIEligibleIndividuals,
        isPIContribution,
        policyEligibleIndividual,
        policyContribution,
        policyInformationType,
        policyNumber,
        policySpecialUnderwritingProvisions,
        isPISpecialUnderwritingProvisions,
        policyEffectiveDate,
    } = usePolicyDataState()

    const currency = 'Philippine Peso (PHP)';

    const { 
        handleCancelEditPolicyInformation, 
        handleCancelEditEffectiveDate,
        handleUpdatePolicyEffectiveDate,
        isLoadingUpdatePolicyEffectiveDate,
    } = usePolicyActions()

    const {
        handleCKEditorSave,
        isCKEditorType,
        getPolicyInformationTitle,
        getPolicyInformationValue,
        onChangeCKEditor,
        isLoadingUpdatePolicy
    } = useCKEditorActions();
    
    return (
        <section className="overflow-hidden rounded border border-gray-200 bg-white">
            <PolicyTabHeader
                title='Policy Information'
                subTitle='Enter the basic information of the policy.'
            />
                <div className="py-3 px-4 space-y-4 lg:space-y-5">
                    <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
                        {/* Policy Number */}
                        <div>
                            <LabelHead label="Policy No."/>
                            <Input
                                type="text"
                                name={`policyNo`}
                                value={policyNumber}
                                isCustomStyle={true}
                                customCss={`${defaultStyle} ${customStyle}`}
                                onChange={(e) => {}}
                                required={true}
                                disabled={true}
                            />
                        </div>
                        {/* Policyholder */}
                        <div>
                            <LabelHead label="Policyholder (Company Name)"/>
                            <Input
                                type="text"
                                name={`policyholderAndAddress`}
                                value={groupName ?? '-'}
                                isCustomStyle={true}
                                customCss={`${defaultStyle} ${customStyle}`}
                                onChange={(e) => {}}
                                required={true}
                                disabled={true}
                            />
                        </div>
                        {/* Effective Date */}
                        <div>
                            <div className="flex items-center justify-between gap-2">
                                <LabelHead label="Effective Date" />
                                {!policyEffectiveDate.isEdit ? (
                                    <button
                                        type="button"
                                        onClick={() => dispatch(setEffectiveDate({ isEdit: true }))}
                                        className="mb-1.5 text-[10px] font-medium text-[#186cb2] transition-colors sm:text-xs"
                                    >
                                        Edit
                                    </button>
                                ) : (
                                    <div className="mb-1.5 flex items-center gap-1.5">
                                        <button
                                            type="button"
                                            onClick={handleCancelEditEffectiveDate}
                                            disabled={isLoadingUpdatePolicyEffectiveDate}
                                            className="text-[10px] font-medium text-gray-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:text-xs"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleUpdatePolicyEffectiveDate}
                                            disabled={isLoadingUpdatePolicyEffectiveDate}
                                            className="inline-flex items-center gap-1 text-[10px] font-medium text-[#186cb2] transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:text-xs"
                                        >
                                            {isLoadingUpdatePolicyEffectiveDate && (
                                                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                                                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                                </svg>
                                            )}
                                            {isLoadingUpdatePolicyEffectiveDate ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <Input
                                type="date"
                                name="effectiveDate"
                                value={policyEffectiveDate.value}
                                isCustomStyle={true}
                                customCss={`${defaultStyle} ${customStyle}`}
                                onChange={(e) => dispatch(setEffectiveDate({ value: e.target.value}))}
                                required={false}
                                disabled={!policyEffectiveDate.isEdit || isLoadingUpdatePolicyEffectiveDate}
                            />
                        </div>
                        {/* Basic Plan */}
                        <div>
                            <LabelHead label="Basic Plan"/>
                            <Input
                                type="text"
                                name={`basicPlan`}
                                value={basicPlan?.name ?? '-'}
                                isCustomStyle={true}
                                customCss={`${defaultStyle} ${customStyle}`}
                                onChange={(e) => {}}
                                required={true}
                                disabled={true}
                            />
                        </div>
                        {/* Riders */}
                        <div>
                            <LabelHead label="Supplementary Benefits/Riders"/>
                            <div className="grid grid-cols-1 gap-2">
                                {riders.map((rider) => (
                                    <div key={rider.id} className="cursor-not-allowed flex items-center gap-2 border border-gray-300 bg-gray-100 px-3 py-2 rounded-md">
                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#186cb2] text-xs text-white">
                                            ✓
                                        </span>
                                        <span className="text-xs font-medium text-gray-700">
                                            {rider.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-1.5 text-[10px] text-gray-400 sm:text-xs">
                                Riders are based on the benefits agreed upon by
                                the client in the booked proposal.
                            </p>
                        </div>
                        {/* Currency */}
                        <div>
                            <LabelHead label="Currency"/>
                            <Input
                                type="text"
                                name={`currency`}
                                value={currency}
                                isCustomStyle={true}
                                customCss={`${defaultStyle} ${customStyle}`}
                                onChange={(e) => {}}
                                required={true}
                                disabled={true}
                            />
                        </div>
                    </div>
                    
                    {/* Eligible Individuals */}
                    {PIEligibleIndividuals && (
                        <PolicyCKEditorTextModal
                            title='Eligible Individuals'
                            description='Specify the individuals who are eligible for coverage under the policy.'
                            isEditing={isPIEligibleIndividuals}
                            onEdit={() => dispatch(setPolicyInformationType("eligibleIndividuals"))}
                            onCancel={handleCancelEditPolicyInformation}
                            onSave={() => {}}
                            value={policyEligibleIndividual}
                        />
                    )}
                    <PolicyInformationClassificationAndEligibility isShow={PIClassificationAndEligibility}/>
                    {/* <PolicyInformationContribution isShow={PIContribution}/> */}
                    <PolicyCKEditorTextModal
                        title='Contribution'
                        description='Define how the premiums will be paid.'
                        isEditing={isPIContribution}
                        onEdit={() => dispatch(setPolicyInformationType("contribution"))}
                        onSave={() => {}}
                        onCancel={handleCancelEditPolicyInformation}
                        value={policyContribution}
                    />  
                    <PolicyInformationParticipation isShow={PIParticipationReq}/>
                    <PolicyInformationUnderwritingProvisions isShow={PIUnderwritingProvisions}/>
                    
                    {/* Special Underwriting provisions */}
                    {PISpecialUnderwritingProvisions && (
                        <PolicyCKEditorTextModal
                            title='Special Underwriting Provisions'
                            description='Enter or update the special underwriting provisions.'
                            isEditing={isPISpecialUnderwritingProvisions}
                            onEdit={() => dispatch(setPolicyInformationType("specialUnderwritingProvisions"))}
                            onSave={() => {}}
                            onCancel={handleCancelEditPolicyInformation}
                            value={policySpecialUnderwritingProvisions}
                        />
                    )}
                </div>
            {/* )} */}
            <CKEditor
                isOpen={isCKEditorType(policyInformationType)}
                title={isCKEditorType(policyInformationType) ? getPolicyInformationTitle(policyInformationType) : "" }
                value={isCKEditorType(policyInformationType) ? getPolicyInformationValue( policyInformationType ) : "" }
                onChange={onChangeCKEditor}
                onClose={handleCancelEditPolicyInformation}
                onSave={handleCKEditorSave}
                isLoading={isLoadingUpdatePolicy}
            />
        </section>
    )
}
